import { useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { Card, KeyValue, Notice, Ticks } from '../components/ui.js'
import { WordPressIcon } from '../components/icons.js'

/**
 * The consent screen a WooCommerce plugin sends its own admin to.
 *
 * Everything this needs arrives in the query string, `state` included — a nonce
 * the WordPress site minted and checks again when the browser comes back, so a
 * crafted link cannot complete a connection that admin never asked for.
 *
 * What goes back is a one-time code, never the store key: a permanent bearer
 * secret does not belong in a URL, where it sits in the site's access log, the
 * browser's history, and any Referer header the next request happens to send.
 * The site's own server trades the code for the key over a call the browser
 * never sees.
 */
export function ConnectWordPressPage(): ReactNode {
  const [params] = useSearchParams()
  const { user } = useAuth()
  const [error, setError] = useState<string | null>(null)

  const siteUrl = params.get('site_url') ?? ''
  const name = params.get('name') ?? ''
  const returnUrl = params.get('return_url') ?? ''
  const state = params.get('state') ?? ''

  const connect = useMutation({
    mutationFn: () =>
      api<{ redirect: string }>('/v1/integrations/wordpress/connect', {
        method: 'POST',
        body: { site_url: siteUrl, name, return_url: returnUrl, state },
      }),
    // A full navigation, not a router push: the destination is the merchant's
    // own WordPress admin, which this app does not route.
    onSuccess: (result) => {
      setError(null)
      window.location.href = result.redirect
    },
    onError: (err) => setError((err as Error).message),
  })

  const host = hostOf(siteUrl)
  const complete = siteUrl !== '' && returnUrl !== '' && state !== '' && host !== ''

  if (!complete) {
    return (
      <div className="connect">
        <Card title="This connect link is incomplete">
          <p className="muted">
            It is missing something a WordPress site always sends. Go back to the Outfalak Checkout settings page in your
            WordPress admin and press <strong>Connect</strong> again.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="connect">
      <Card>
        <div className="connect__head">
          <span className="feature__icon" style={{ ['--tone' as string]: 'var(--brand)', width: 48, height: 48 }}>
            <WordPressIcon size={26} />
          </span>
          <div>
            <h1>Connect this store</h1>
            <p className="muted">
              <strong>{host}</strong> wants to connect to your Outfalak Checkout account.
            </p>
          </div>
        </div>

        <KeyValue
          rows={[
            { label: 'Site', value: host },
            { label: 'Store name', value: name || host },
            { label: 'Signed in as', value: user?.email ?? '—' },
          ]}
        />

        <h2 className="connect__sub">What this allows</h2>
        <Ticks
          brand
          items={[
            'Your checkout settings are pushed to this site',
            'Orders placed there appear in this dashboard',
            'The site can look up PIN codes through Outfalak',
          ]}
        />

        {error && (
          <Notice kind="error" title="Could not connect">
            {error}
          </Notice>
        )}

        <div className="connect__actions">
          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={connect.isPending}
            onClick={() => connect.mutate()}
          >
            {connect.isPending ? 'Connecting…' : 'Connect'}
          </button>
          <a className="btn btn--block" href={returnUrl}>
            Cancel
          </a>
        </div>

        <p className="muted connect__foot">
          A store key is issued to this site. You can rotate it any time from Integrations → WordPress.
        </p>
      </Card>
    </div>
  )
}

/** `priyastore.com` out of `https://www.PriyaStore.com/`. */
function hostOf(raw: string): string {
  const value = raw.trim()
  if (value === '') return ''

  try {
    const host = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`).hostname.toLowerCase()
    return host.startsWith('www.') ? host.slice(4) : host
  } catch {
    return ''
  }
}
